import { API_URL } from '../config/config';
import { getRequest } from '../utils/axiosUtils';

/**
 * Profile Activations data — GET /admin/analytics/stat/getProfileActivations.
 *
 * Returns newly ACTIVATED profiles (the canonical APPROVED status, dated by signup day)
 * split men vs women across three granularities: `daily` (last 45 days), `weekly`
 * (last 13 iso-weeks), `monthly` (last 13 months), each an array of
 * { lab, sub, m, f, tip, cur }, plus `kpi` (today, l7/p7, l30/p30, w30m/w30f).
 *
 * For the retention panels it also returns:
 *   coh[basis][gender][i] — 60 daily cohorts. basis is `act` (dated by activation) or
 *     `sign` (dated by signup); gender is o/m/f and o === m + f. Each row is
 *     { iso, lab, act[], size } for act, or { iso, lab, act[], na[], S } for sign,
 *     where act[k] is how many were approved k days on, na[k] how many activated that
 *     day, and the arrays run only as far as the days that have actually elapsed.
 *   attr[gender][k] — average share of an activation cohort still approved on day k.
 *
 * NOTE: the cohort panels use a different population to the bars (a cohort is fixed on
 * its day 0, whatever the member's status is now), so they deliberately do NOT tie out
 * to the daily bars. See the server's loadCohortWindow().
 */
export const getProfileActivations = async () => {
  const res = await getRequest({
    url: `${API_URL}/admin/analytics/stat/getProfileActivations`,
    isAbort: false,
  });
  if (!res) return null;
  if (res.status >= 200 && res.status < 300 && res.data) return res.data.data;
  throw new Error(`Unexpected response code: ${res.status}`);
};

/**
 * The real members behind one cohort-day cell — who newly activated, who came back, and
 * who went inactive (left approved). Backs both the summary under the table and the day-detail page.
 *
 * `cur` is computed from the same window as the table, so it always equals the number
 * printed on the cell that was clicked.
 *
 * isAbort:false — the admin's getRequest cancels in-flight calls keyed on URL path, so
 * a shared path would let one click kill the report fetch (the trap Ghost Funnel hit).
 */
export const getActivationCohortMembers = async ({ basis, gender, iso, day }) => {
  const q = new URLSearchParams({ basis, gender, iso, day: String(day) }).toString();
  const res = await getRequest({
    url: `${API_URL}/admin/analytics/stat/getActivationCohortMembers?${q}`,
    isAbort: false,
  });
  if (!res) return null;
  if (res.status >= 200 && res.status < 300 && res.data) return res.data.data;
  throw new Error(`Unexpected response code: ${res.status}`);
};
